import { useEffect, useState } from 'react'
import { collectionGroup, onSnapshot } from 'firebase/firestore'
import { db } from '@/config/firebase'

interface StagesWithFilesState {
  /** projectId -> set of stage numbers that have at least one uploaded file. */
  stagesWithFiles: Record<string, Set<number>>
  loading: boolean
}

/**
 * Realtime, admin-only aggregate of which stages have uploaded documents, keyed
 * by project. Relies on the collectionGroup('files') read rule (firestore.rules)
 * and powers the overview's "documents awaiting review" indicator. On error
 * (e.g. a non-admin), it resolves to an empty map rather than throwing.
 */
export function useStagesWithFiles(): StagesWithFilesState {
  const [state, setState] = useState<StagesWithFilesState>({
    stagesWithFiles: {},
    loading: true,
  })

  useEffect(() => {
    const filesQuery = collectionGroup(db, 'files')
    const unsubscribe = onSnapshot(
      filesQuery,
      (snapshot) => {
        const map: Record<string, Set<number>> = {}
        snapshot.forEach((docSnap) => {
          const projectId = docSnap.ref.parent.parent?.id
          const stage = (docSnap.data() as { stage?: number }).stage
          if (!projectId || typeof stage !== 'number') {
            return
          }
          ;(map[projectId] ??= new Set<number>()).add(stage)
        })
        setState({ stagesWithFiles: map, loading: false })
      },
      () => setState({ stagesWithFiles: {}, loading: false }),
    )
    return unsubscribe
  }, [])

  return state
}
